import { useState, useEffect } from 'react';
import { customerService } from '../../../services/customerService';
import { API_URL } from '../../../config/environment';
import api from '../../../services/api';
import { useWarehouses, useInventoryProducts, useProductSearch, useSelectedProducts } from '../WarehouseDocumentBase';
import { useLocation } from '../../../contexts/LocationContext';

export const useExternalIssueData = () => {
  const { locationId } = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);

  // Customer state
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [notes, setNotes] = useState('');
  const [documentDate, setDocumentDate] = useState(new Date().toISOString().split('T')[0]);

  // Warehouse
  const { warehouses, selectedWarehouse, setSelectedWarehouse } = useWarehouses(locationId);

  // Products from warehouse
  const { products, loadingProducts, loadProducts } = useInventoryProducts(selectedWarehouse);

  // Debounced search
  useProductSearch(showProductModal, searchTerm, loadProducts, [selectedWarehouse]);

  // Selected products
  const {
    selectedProducts,
    addProduct: baseAddProduct,
    updateQuantity: updateProductQuantity,
    updateReason: updateProductReason,
    removeProduct,
    clearProducts,
  } = useSelectedProducts(setError);

  // Issue history state
  const [issues, setIssues] = useState([]);
  const [activeTab, setActiveTab] = useState('new');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Load customers
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        // getCustomers zwraca {items,total,page,per_page} - ani tablica, ani .data.
        // Bez tej poprawki lista klientow przy WZ byla zawsze pusta.
        // rola='odbiorca': WZ wystawiamy klientowi, nie dostawcy z kartoteki.
        const result = await customerService.getCustomers({ rola: 'odbiorca', per_page: 200 });
        const items = result?.items || (Array.isArray(result) ? result : []);
        setCustomers(items);
      } catch (err) {
        console.error('Błąd ładowania klientów:', err);
      }
    };
    loadCustomers();
  }, []);

  useEffect(() => {
    if (activeTab === 'history' && locationId) {
      loadIssues();
    }
  }, [activeTab, locationId]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadIssues();
    }
  }, [dateFilter, statusFilter]);

  const loadIssues = async () => {
    try {
      const params = new URLSearchParams();
      if (locationId) params.append('location_id', locationId);
      if (dateFilter) params.append('date_from', dateFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await api.get(`/warehouse/external-issue/list?${params}`);
      const data = response.data;
      if (data.success) {
        setIssues(data.data || []);
      }
    } catch (err) {
      console.error('Błąd ładowania historii WZ:', err);
    }
  };

  const addProduct = (product) => {
    const unitPrice = product.cena_zakupu || product.price || 0;
    baseAddProduct(product, { unit_price: unitPrice });
    setShowProductModal(false);
    setSearchTerm('');
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowCustomerModal(false);
  };

  const handleAddNewCustomer = (newCustomer) => {
    setCustomers((prev) => [...prev, newCustomer]);
    setSelectedCustomer(newCustomer);
    setShowAddCustomerModal(false);
  };

  const clearSelectedCustomer = () => {
    setSelectedCustomer(null);
  };

  const getCustomerDisplayName = (customer) => {
    if (!customer) return '';
    if (customer.nazwa_firmy) return customer.nazwa_firmy;
    return `${customer.imie || ''} ${customer.nazwisko || ''}`.trim();
  };

  const getCustomerAddress = (customer) => {
    if (!customer) return '';
    const parts = [];
    if (customer.adres) parts.push(customer.adres);
    if (customer.kod_pocztowy) parts.push(customer.kod_pocztowy);
    if (customer.miasto) parts.push(customer.miasto);
    return parts.join(', ');
  };

  const filteredCustomers = (Array.isArray(customers) ? customers : []).filter((customer) => {
    const fullName = `${customer.imie || ''} ${customer.nazwisko || ''}`.toLowerCase();
    const companyName = (customer.nazwa_firmy || '').toLowerCase();
    const email = (customer.email || '').toLowerCase();
    const nip = (customer.nip || '').toLowerCase();
    const searchLower = customerSearch.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      companyName.includes(searchLower) ||
      email.includes(searchLower) ||
      nip.includes(searchLower)
    );
  });

  const calculateTotal = () =>
    selectedProducts.reduce((sum, p) => sum + p.quantity * (p.unit_price || 0), 0);

  const submitExternalIssue = async () => {
    if (selectedProducts.length === 0) {
      setError('Dodaj przynajmniej jeden produkt');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/warehouse/external-issue', {
        products: selectedProducts.map((p) => ({
          product_id: p.id,
          product_name: p.name || p.nazwa,
          quantity: p.quantity,
          unit: p.jednostka || 'szt.',
          unit_price: p.unit_price || 0,
          reason: p.reason || 'Wydanie zewnętrzne',
        })),
        location_id: locationId,
        customer_id: selectedCustomer?.id,
        customer_name: getCustomerDisplayName(selectedCustomer),
        customer_nip: selectedCustomer?.nip || '',
        customer_address: getCustomerAddress(selectedCustomer),
        notes,
        source_type: 'manual',
        document_date: documentDate,
      });

      const result = response.data;

      if (result.success) {
        setSuccess(
          `Wydanie zewnętrzne ${result.data.document_number} zostało zarejestrowane pomyślnie`
        );
        clearProducts();
        setSelectedCustomer(null);
        setNotes('');
        loadIssues();
        setTimeout(() => setSuccess(''), 5000);
        loadProducts();
      } else {
        setError(result.error || 'Błąd podczas rejestrowania wydania');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const showIssueDetails = async (issue) => {
    setLoadingDetails(true);
    setShowDetailsModal(true);
    try {
      const response = await api.get(`/warehouse/external-issue/${issue.id}`);
      const data = response.data;
      if (data.success) {
        setSelectedIssue(data.data);
      }
    } catch (err) {
      console.error('Błąd ładowania szczegółów:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const printIssue = (issueId) => {
    window.open(`${API_URL}/warehouse/external-issue/${issueId}/pdf`, '_blank');
  };

  return {
    locationId,
    searchTerm,
    setSearchTerm,
    loading,
    error,
    setError,
    success,
    setSuccess,
    showProductModal,
    setShowProductModal,
    customers,
    selectedCustomer,
    showCustomerModal,
    setShowCustomerModal,
    showAddCustomerModal,
    setShowAddCustomerModal,
    customerSearch,
    setCustomerSearch,
    notes,
    setNotes,
    warehouses,
    selectedWarehouse,
    setSelectedWarehouse,
    products,
    loadingProducts,
    selectedProducts,
    updateProductQuantity,
    updateProductReason,
    removeProduct,
    issues,
    activeTab,
    setActiveTab,
    dateFilter,
    setDateFilter,
    statusFilter,
    setStatusFilter,
    showDetailsModal,
    setShowDetailsModal,
    selectedIssue,
    setSelectedIssue,
    loadingDetails,
    filteredCustomers,
    calculateTotal,
    addProduct,
    handleSelectCustomer,
    handleAddNewCustomer,
    clearSelectedCustomer,
    getCustomerDisplayName,
    getCustomerAddress,
    submitExternalIssue,
    showIssueDetails,
    printIssue,
    loadIssues,
    documentDate,
    setDocumentDate,
  };
};
